import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FD100MultiSelectOptions } from '../../Plateforme/Utils/Listings';
import FabFrIndicator from '../../Plateforme/Atoms/FabFrIndicator';
import { getEventDesignerDisplayName } from './utils/eventDesignerHelpers';
import PieceDetailsPopup from './PieceDetailsPopup';
import CandidatureCompletePopup from './CandidatureCompletePopup';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import dayjs from 'dayjs';

const ProjectsSection = styled.div`
  margin-top: 40px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
`;

const FiltersContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 20px;
  padding: 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FilterLabel = styled.label`
  font-weight: 600;
  font-size: 14px;
  color: #333;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #0000FF;
  }
`;

const StatsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 20px 0;
  padding: 15px 20px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const StatsText = styled.span`
  font-weight: 600;
  color: #333;
  font-size: 16px;
`;

const ExportContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const ExportButton = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  
  &.excel {
    background: #217346;
    color: white;
    
    &:hover {
      background: #1e5e3a;
    }
  }
  
  &.pdf {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #b02a37;
    }
  }
`;

const ProjectsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  th:nth-child(1) { width: 25%; } /* Entreprise */
  th:nth-child(2) { width: 25%; } /* Pièce/Projet */
  th:nth-child(3) { width: 15%; } /* Catégorie */
  th:nth-child(4) { width: 15%; } /* Aperçu */
  th:nth-child(5) { width: 12%; } /* Statut */
  th:nth-child(6) { width: 8%; } /* Actions */
`;

const TableHeader = styled.thead`
  background: #0000FF;
  color: white;
  
  th {
    padding: 15px;
    text-align: left;
    font-weight: 600;
    font-size: 14px;
  }
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid #e0e0e0;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: #f5f5f5;
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  td {
    padding: 15px;
    font-size: 14px;
    vertical-align: top;
  }
`;

const ProjectRow = styled.tr`
  ${props => props.verified && `background-color: #ebf7eb;`}
`;

const StatusIndicator = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 10px;
  
  ${props => {
    switch(props.status) {
      case 'validé':
        return 'background-color: #28a745;';
      case 'refusé':
        return 'background-color: #dc3545;';
      default:
        return 'background-color: #ffc107;';
    }
  }}
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  ${props => {
    switch(props.status) {
      case 'validé':
        return 'background: #28a745; color: white;';
      case 'refusé':
        return 'background: #dc3545; color: white;';
      case 'en attente':
        return 'background: #6c757d; color: white;';
      default:
        return 'background: #ffc107; color: #000;';
    }
  }}

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  }
`;

const CompanyName = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 15px;
  color: #2c3e50;
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CompanyLink = styled(Link)`
  color: #0000FF;
  text-decoration: none;
  font-size: 12px;
  opacity: 0.8;
  
  &:hover {
    text-decoration: underline;
    opacity: 1;
  }
`;

const CompanyTag = styled.div`
  margin-top: 4px;
`;

const ProjectName = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  color: #2c3e50;
`;

const ProjectInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProjectDescription = styled.div`
  font-size: 12px;
  color: #6c757d;
  font-style: italic;
  margin-top: 4px;
  max-height: 40px;
  overflow: hidden;
  line-height: 1.3;
`;

const PartnerInfo = styled.div`
  font-size: 12px;
  color: #495057;
  margin-top: 6px;
  padding: 6px 10px;
  background: #f8f9fa;
  border-left: 3px solid #0000FF;
  border-radius: 4px;
  
  strong {
    color: #0000FF;
    font-weight: 600;
  }
`;

const CategorySelect = styled.select`
  padding: 4px 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 12px;
  margin-top: 5px;
`;

const CategoryDisplay = styled.div`
  padding: 6px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-size: 13px;
  color: #495057;
  font-weight: 500;
  min-height: 20px;
  display: flex;
  align-items: center;
`;

const StatusDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 4px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  z-index: 100;
  min-width: 120px;
  overflow: hidden;
`;

const StatusOption = styled.div`
  padding: 8px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f0f0f0;
  }

  &.selected {
    background-color: #e8f4ff;
    font-weight: 600;
  }
`;

const StatusContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  margin: 5px 0;
`;

const LinkButton = styled(Link)`
  color: #0000FF;
  text-decoration: none;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ClickableButton = styled.button`
  color: #0000FF;
  text-decoration: none;
  font-weight: 500;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  text-align: left;
  
  &:hover {
    text-decoration: underline;
  }
`;

const CategoryWarning = styled.div`
  color: #dc3545;
  font-size: 12px;
  font-weight: 500;
`;

const DesignerTag = styled.div`
  color: #0000FF;
  font-size: 12px;
  font-weight: 500;
`;

const NoProjectsMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-style: italic;
  background: white;
  border-radius: 8px;
`;

const MediaPreview = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 12px;
  overflow: hidden;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: all 0.3s ease;
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 20px rgba(0, 0, 255, 0.15);
    border-color: #0000FF;
  }
`;

const MediaImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
  
  ${MediaPreview}:hover & {
    transform: scale(1.1);
  }
`;

const MediaPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #adb5bd;
  font-size: 12px;
  text-align: center;
  padding: 8px;
`;

const MediaIcon = styled.div`
  font-size: 24px;
  margin-bottom: 4px;
  opacity: 0.7;
`;

const MediaCount = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  background: linear-gradient(135deg, #0000FF 0%, #0000CC 100%);
  color: white;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 3px 8px rgba(0, 0, 255, 0.3);
  border: 2px solid white;
`;

const ModernFDProjetsList = (props) => {
  const { i18n } = useTranslation();
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [categorieFilter, setCategorieFilter] = useState("");
  const [verificationFilter, setVerificationFilter] = useState("");
  const [sortBy, setSortBy] = useState("participationCreationDate");
  const [selectedPiece, setSelectedPiece] = useState(null);
  const [selectedCandidature, setSelectedCandidature] = useState(null);
  const [editingStatus, setEditingStatus] = useState(null);

  const tyoplogieEspaceFD100 = FD100MultiSelectOptions.tyoplogieEspaceFD100("fr");
  const typologieObjetFD100 = FD100MultiSelectOptions.typologieObjetFD100("fr");

  const FDCategories = [
    ...tyoplogieEspaceFD100,
    ...typologieObjetFD100,
  ].filter((cat, index, self) => self.findIndex((t) => t.value === cat.value) === index);

  const quentinCase = (string) => {
    if (!string) return '';
    return string.toLowerCase().split(' ').map((s) => s.charAt(0).toUpperCase() + s.substring(1)).join(' ');
  };

  const badCategoryVia = (cat_via_string) => {
    if (!cat_via_string) return "";
    if (cat_via_string.toLowerCase().includes("fabricant") || 
        cat_via_string.toLowerCase().includes("éditeur") || 
        cat_via_string.toLowerCase().includes("editeur")) {
      return cat_via_string.split(";").join(" / ");
    }
    return "";
  };

  const getDesignerTag = (cat_via_string) => {
    if (!cat_via_string) return "";
    // For Milan 2026 data, check if it's Designer type
    if (cat_via_string === "Designer") {
      return "Designer";
    }
    // For legacy data, check if it's not fabricant/éditeur, then it's likely a designer
    if (!cat_via_string.toLowerCase().includes("fabricant") && 
        !cat_via_string.toLowerCase().includes("éditeur") && 
        !cat_via_string.toLowerCase().includes("editeur")) {
      return "Designer";
    }
    return "";
  };

  const getProjectStatus = (projet) => {
    if (projet.status === "validé") return "validé";
    if (projet.status === "refusé") return "refusé";
    if (projet.status === "en attente") return "en attente";
    return "à valider";
  };

  const getMediaPreview = (projet) => {
    // For Milan 2026 projects, check if media data is available
    const mediaData = projet.uploadedMedia;
    if (mediaData && mediaData.length > 0) {
      const firstMedia = mediaData[0];
      const mediaBaseUrl = process.env.REACT_APP_MEDIA_BASE_URL;

      // Handle images
      if (firstMedia.mimetype && firstMedia.mimetype.startsWith('image/')) {
        return {
          url: `${mediaBaseUrl}/${firstMedia.path}`,
          count: mediaData.length,
          type: firstMedia.mimetype
        };
      }
      // Handle PDFs with preview image (like Slider.js)
      else if (firstMedia.mimetype && firstMedia.mimetype.includes('pdf')) {
        // Generate preview path like Slider.js: firstpage_[filename].png
        const filename = firstMedia.path.split('/').pop();
        const pdfPreviewPath = firstMedia.path.replace(filename, "firstpage_" + filename).replace('.pdf', '.png');
        return {
          url: `${mediaBaseUrl}/${pdfPreviewPath}`,
          originalUrl: `${mediaBaseUrl}/${firstMedia.path}`,
          count: mediaData.length,
          type: firstMedia.mimetype,
          isPdf: true
        };
      }
      // Handle videos with thumbnail or video element
      else if (firstMedia.mimetype && firstMedia.mimetype.startsWith('video/')) {
        return {
          url: `${mediaBaseUrl}/${firstMedia.path}`,
          count: mediaData.length,
          type: firstMedia.mimetype,
          isVideo: true
        };
      }
      else {
        // For other file types, show a generic file icon
        return {
          url: null,
          count: mediaData.length,
          type: firstMedia.mimetype,
          isFile: true
        };
      }
    }
    return null;
  };

  const sortProjectsByParticipationCreationDate = (a, b) => {
    const dateA = new Date(a.user.participation.createdAt);
    const dateB = new Date(b.user.participation.createdAt);
    
    if (dateA < dateB) return 1;
    if (dateA > dateB) return -1;
    
    if (a.projet.name < b.projet.name) return -1;
    if (a.projet.name > b.projet.name) return 1;
    
    return a.projet?._id?.localeCompare(b.projet?._id);
  };

  const sortProjectsByCompanyName = (a, b) => {
    const companyA = getEventDesignerDisplayName(a.projet, a.user).toLowerCase();
    const companyB = getEventDesignerDisplayName(b.projet, b.user).toLowerCase();
    if (companyA < companyB) return 1;
    if (companyA > companyB) return -1;
    
    if (a.projet.name < b.projet.name) return 1;
    if (a.projet.name > b.projet.name) return -1;
    
    return a.projet?._id?.localeCompare(b.projet?._id);
  };

  const handlePieceClick = (participationId, pieceId) => {
    console.log("handlePieceClick", participationId, pieceId);
    setSelectedPiece({ participationId, pieceId });
  };

  const handleClosePiecePopup = () => {
    setSelectedPiece(null);
  };

  const handleCandidatureClick = (participationId) => {
    setSelectedCandidature(participationId);
  };

  const handleCloseCandidaturePopup = () => {
    setSelectedCandidature(null);
  };

  const handleStatusChange = async (participationId, projetId, newStatus) => {
    // Call the parent's updateProjetStatus function
    if (props.updateProjetStatus) {
      await props.updateProjetStatus(participationId, projetId, newStatus);
    }
    setEditingStatus(null);
  };

  // Handle click outside to close dropdown
  const handleClickOutside = (e) => {
    if (!e.target.closest('.status-container')) {
      setEditingStatus(null);
    }
  };

  // Add event listener for click outside
  React.useEffect(() => {
    if (editingStatus) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [editingStatus]);

  const handleExportExcel = () => {
    try {
      // Prepare data for export
      const exportData = filteredProjects.map((projet) => {
        const status = getProjectStatus(projet.projet);
        const companyName = getEventDesignerDisplayName(projet.projet, projet.user);
        const categoryVia = projet.user.hsCompany?.properties?.cat_gorie_via || '';
        const isDesigner = getDesignerTag(categoryVia);
        const isFabricant = badCategoryVia(categoryVia);
        
        // Base export data
        const baseData = {
          'Date de candidature': dayjs(projet.user.participation.createdAt).format('DD/MM/YYYY HH:mm'),
          'Entreprise': companyName,
          'Type': isDesigner || isFabricant || categoryVia,
          'Nom de la pièce': quentinCase(i18n.language !== 'fr' && projet.projet.name_en ? projet.projet.name_en : projet.projet.name),
          'Nom de la pièce (EN)': projet.projet.name_en || '',
          'Description': projet.projet.description || '',
          'Catégorie': FDCategories.find(cat => cat.value === projet.projet.typologie)?.label || projet.projet.typologie || '',
          'Type (Espace/Objet)': projet.projet.type || '',
          'Fabrication française': projet.projet.fabrication_fr === 'oui' ? 'Oui' : 'Non',
          'Statut': status === 'à valider' ? 'À valider' : status.charAt(0).toUpperCase() + status.slice(1),
          'Vérifié': projet.projet.projet_verifie_fd100_2025 === 'oui' ? 'Oui' : 'Non',
          'Email': projet.user.user.email || '',
          'ID Participation': projet.user.participation._id,
          'ID Projet': projet.projet._id
        };

        // Add Milan 2026 specific partner information
        if (props.event?.title_fr?.includes('Milan')) {
          const submissionUserType = projet.projet.submissionUserType;
          const designerName = projet.projet.designerCompanyName;
          const fabricantName = projet.projet.fabricantCompanyName;
          
          baseData['Type de soumetteur'] = submissionUserType === 'designer' ? 'Designer' : 'Fabricant/Éditeur';
          baseData['Partenaire Designer'] = designerName || '';
          baseData['Partenaire Fabricant'] = fabricantName || '';
        }

        return baseData;
      });

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, props.event?.title_fr?.includes('Milan') ? 'Milan 2026' : 'Projets FD100');

      // Auto-size columns
      const colWidths = Object.keys(exportData[0] || {}).map(key => ({
        wch: Math.max(key.length, ...exportData.map(row => String(row[key] || '').length)) + 2
      }));
      ws['!cols'] = colWidths;

      // Generate filename
      const filename = `Export_${props.event?.title_fr?.replace(/\s+/g, '_') || 'FD100'}_${dayjs().format('YYYY-MM-DD_HH-mm')}.xlsx`;

      // Download file
      XLSX.writeFile(wb, filename);
    } catch (error) {
      console.error('Error exporting Excel:', error);
      alert('Erreur lors de l\'export Excel');
    }
  };

  const handleExportPDF = () => {
    try {
      // Create a printable version of the table
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Veuillez autoriser les pop-ups pour exporter en PDF');
        return;
      }

      const eventTitle = props.event?.title_fr || 'Export';
      const exportDate = dayjs().format('DD MMMM YYYY à HH:mm');
      
      // Build HTML content
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${eventTitle} - Export PDF</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              font-size: 12px;
            }
            h1 {
              color: #0000FF;
              font-size: 24px;
              margin-bottom: 10px;
            }
            .header-info {
              margin-bottom: 20px;
              color: #666;
              font-size: 14px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            th {
              background-color: #0000FF;
              color: white;
              padding: 10px;
              text-align: left;
              font-weight: 600;
            }
            td {
              border-bottom: 1px solid #ddd;
              padding: 8px;
              vertical-align: top;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            .status-badge {
              display: inline-block;
              padding: 3px 8px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 600;
            }
            .status-valide { background: #28a745; color: white; }
            .status-refuse { background: #dc3545; color: white; }
            .status-avalider { background: #ffc107; color: #000; }
            .company-name {
              font-weight: 600;
              margin-bottom: 4px;
            }
            .piece-name {
              font-weight: 600;
              margin-bottom: 4px;
            }
            .description {
              font-size: 11px;
              color: #666;
              font-style: italic;
            }
            @media print {
              body { margin: 10px; }
              table { page-break-inside: auto; }
              tr { page-break-inside: avoid; page-break-after: auto; }
            }
          </style>
        </head>
        <body>
          <h1>${eventTitle}</h1>
          <div class="header-info">
            <div>Export généré le ${exportDate}</div>
            <div>${filteredProjects.length} projet(s) trouvé(s)</div>
          </div>
          <table>
            <thead>
              <tr>
                <th style="width: 25%">Entreprise</th>
                <th style="width: 25%">${props.event?.title_fr?.includes('Milan') ? 'Pièce' : 'Projet'}</th>
                <th style="width: 15%">Catégorie</th>
                <th style="width: 15%">Type</th>
                <th style="width: 10%">Statut</th>
                <th style="width: 10%">Vérifié</th>
              </tr>
            </thead>
            <tbody>
              ${filteredProjects.map((projet) => {
                const status = getProjectStatus(projet.projet);
                const companyName = getEventDesignerDisplayName(projet.projet, projet.user);
                const categoryLabel = FDCategories.find(cat => cat.value === projet.projet.typologie)?.label || projet.projet.typologie || '-';
                const statusClass = status === 'validé' ? 'status-valide' : 
                                   status === 'refusé' ? 'status-refuse' : 'status-avalider';
                const statusText = status === 'à valider' ? 'À valider' : status.charAt(0).toUpperCase() + status.slice(1);
                const isVerified = projet.projet.projet_verifie_fd100_2025 === 'oui';
                
                return `
                  <tr>
                    <td>
                      <div class="company-name">${companyName}</div>
                      ${projet.user.hsCompany?.properties?.cat_gorie_via ? 
                        `<div style="font-size: 11px; color: #666;">${projet.user.hsCompany.properties.cat_gorie_via}</div>` : ''}
                    </td>
                    <td>
                      <div class="piece-name">${quentinCase(i18n.language !== 'fr' && projet.projet.name_en ? projet.projet.name_en : projet.projet.name)}</div>
                      ${projet.projet.description ? 
                        `<div class="description">${projet.projet.description.substring(0, 120)}${projet.projet.description.length > 120 ? '...' : ''}</div>` : ''}
                      ${(() => {
                        // Add partner info for Milan 2026
                        if (eventTitle.includes('Milan')) {
                          const submissionUserType = projet.projet.submissionUserType;
                          const designerName = projet.projet.designerCompanyName;
                          const fabricantName = projet.projet.fabricantCompanyName;
                          
                          if (submissionUserType === 'designer' && fabricantName && fabricantName !== companyName) {
                            return `<div style="font-size: 11px; color: #007bff; margin-top: 3px;">Fabricant - ${fabricantName}</div>`;
                          } else if (submissionUserType === 'fabricant' && designerName && designerName !== companyName) {
                            return `<div style="font-size: 11px; color: #007bff; margin-top: 3px;">Designer - ${designerName}</div>`;
                          }
                        }
                        return '';
                      })()}
                    </td>
                    <td>${categoryLabel}</td>
                    <td>${projet.projet.type || '-'}</td>
                    <td><span class="status-badge ${statusClass}">${statusText}</span></td>
                    <td>${isVerified ? '✓' : '-'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
        </body>
        </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          // Don't close immediately to allow user to complete print dialog
        }, 250);
      };
    } catch (error) {
      console.error('Error exporting PDF:', error);
      alert('Erreur lors de l\'export PDF');
    }
  };

  const handleExportDetailedPDF = () => {
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Veuillez autoriser les pop-ups pour exporter en PDF');
        return;
      }

      const eventTitle = props.event?.title_fr || 'Export';
      const exportDate = dayjs().format('DD MMMM YYYY à HH:mm');
      
      // Build detailed HTML content similar to FormSubmissionDetail
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${eventTitle} - Export PDF Détaillé</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              font-size: 11px;
              line-height: 1.4;
            }
            h1 {
              color: #0000FF;
              font-size: 20px;
              margin-bottom: 10px;
              page-break-after: avoid;
            }
            h2 {
              color: #0000FF;
              font-size: 16px;
              margin-top: 25px;
              margin-bottom: 15px;
              page-break-after: avoid;
            }
            h3 {
              color: #333;
              font-size: 14px;
              margin-top: 20px;
              margin-bottom: 10px;
              page-break-after: avoid;
            }
            .header-info {
              margin-bottom: 20px;
              color: #666;
              font-size: 12px;
            }
            .project-section {
              background: #f8f9fa;
              padding: 15px;
              border-radius: 5px;
              margin-bottom: 20px;
              page-break-inside: avoid;
              border: 1px solid #e0e0e0;
            }
            .data-grid {
              display: grid;
              grid-template-columns: 140px 1fr;
              gap: 8px;
              margin-bottom: 10px;
            }
            .data-label {
              font-weight: 600;
              color: #555;
            }
            .data-value {
              color: #333;
              word-wrap: break-word;
            }
            .status-badge {
              display: inline-block;
              padding: 3px 8px;
              border-radius: 12px;
              font-size: 10px;
              font-weight: 600;
            }
            .status-valide { background: #28a745; color: white; }
            .status-refuse { background: #dc3545; color: white; }
            .status-avalider { background: #ffc107; color: #000; }
            .company-header {
              background: #e7f3ff;
              padding: 12px;
              border-radius: 5px;
              margin-bottom: 15px;
              border-left: 4px solid #0000FF;
            }
            .company-name {
              font-weight: 700;
              font-size: 14px;
              color: #0000FF;
              margin-bottom: 5px;
            }
            .partner-info {
              background: #f0f8ff;
              padding: 8px;
              border-radius: 4px;
              margin-top: 5px;
              font-size: 10px;
              color: #007bff;
              border-left: 3px solid #007bff;
            }
            @media print {
              body { margin: 10px; font-size: 10px; }
              .project-section { page-break-inside: avoid; }
              h1, h2, h3 { page-break-after: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>${eventTitle} - Export Détaillé</h1>
          <div class="header-info">
            <div>Export généré le ${exportDate}</div>
            <div>${filteredProjects.length} projet(s) trouvé(s)</div>
          </div>
          
          ${filteredProjects.map((projet, index) => {
            const status = getProjectStatus(projet.projet);
            const companyName = getEventDesignerDisplayName(projet.projet, projet.user);
            const categoryLabel = FDCategories.find(cat => cat.value === projet.projet.typologie)?.label || projet.projet.typologie || '-';
            const statusClass = status === 'validé' ? 'status-valide' : 
                               status === 'refusé' ? 'status-refuse' : 'status-avalider';
            const statusText = status === 'à valider' ? 'À valider' : status.charAt(0).toUpperCase() + status.slice(1);
            const isVerified = projet.projet.projet_verifie_fd100_2025 === 'oui';
            const submissionDate = dayjs(projet.user.participation.createdAt).format('DD/MM/YYYY HH:mm');
            
            return `
              <div class="project-section">
                <div class="company-header">
                  <div class="company-name">
                    ${index + 1}. ${companyName}
                  </div>
                  <div style="font-size: 11px; color: #666;">
                    ${projet.user.hsCompany?.properties?.cat_gorie_via || ''}
                  </div>
                </div>
                
                <h3>${props.event?.title_fr?.includes('Milan') ? 'Pièce' : 'Projet'}: ${quentinCase(i18n.language !== 'fr' && projet.projet.name_en ? projet.projet.name_en : projet.projet.name)}</h3>
                
                <div class="data-grid">
                  <div class="data-label">Date candidature:</div>
                  <div class="data-value">${submissionDate}</div>
                  
                  <div class="data-label">Email:</div>
                  <div class="data-value">${projet.user.user.email || ''}</div>
                  
                  <div class="data-label">Statut:</div>
                  <div class="data-value"><span class="status-badge ${statusClass}">${statusText}</span></div>
                  
                  <div class="data-label">Vérifié:</div>
                  <div class="data-value">${isVerified ? 'Oui' : 'Non'}</div>
                  
                  <div class="data-label">Catégorie:</div>
                  <div class="data-value">${categoryLabel}</div>
                  
                  <div class="data-label">Type:</div>
                  <div class="data-value">${projet.projet.type || '-'}</div>
                  
                  <div class="data-label">Fabrication FR:</div>
                  <div class="data-value">${projet.projet.fabrication_fr === 'oui' ? 'Oui' : 'Non'}</div>
                  
                  ${projet.projet.name_en ? `
                  <div class="data-label">Nom (EN):</div>
                  <div class="data-value">${projet.projet.name_en}</div>
                  ` : ''}
                  
                  ${projet.projet.description ? `
                  <div class="data-label">Description:</div>
                  <div class="data-value">${projet.projet.description}</div>
                  ` : ''}
                  
                  <div class="data-label">ID Participation:</div>
                  <div class="data-value">${projet.user.participation._id}</div>
                  
                  <div class="data-label">ID Projet:</div>
                  <div class="data-value">${projet.projet._id}</div>
                </div>
                
                ${(() => {
                  // Add partner info for Milan 2026
                  if (eventTitle.includes('Milan')) {
                    const submissionUserType = projet.projet.submissionUserType;
                    const designerName = projet.projet.designerCompanyName;
                    const fabricantName = projet.projet.fabricantCompanyName;
                    
                    let partnerInfo = '';
                    if (submissionUserType === 'designer' && fabricantName && fabricantName !== companyName) {
                      partnerInfo = `<div class="partner-info"><strong>Partenaire Fabricant:</strong> ${fabricantName}</div>`;
                    } else if (submissionUserType === 'fabricant' && designerName && designerName !== companyName) {
                      partnerInfo = `<div class="partner-info"><strong>Partenaire Designer:</strong> ${designerName}</div>`;
                    }
                    
                    return partnerInfo + `
                      <div class="data-grid" style="margin-top: 15px;">
                        <div class="data-label">Type soumetteur:</div>
                        <div class="data-value">${submissionUserType === 'designer' ? 'Designer' : 'Fabricant/Éditeur'}</div>
                      </div>
                    `;
                  }
                  return '';
                })()}
                
                ${projet.projet.uploadedMedia && projet.projet.uploadedMedia.length > 0 ? `
                <div style="margin-top: 15px;">
                  <div class="data-label" style="margin-bottom: 8px;">Médias uploadés (${projet.projet.uploadedMedia.length}):</div>
                  ${projet.projet.uploadedMedia.map((media) => `
                    <div style="font-size: 10px; color: #666; margin-bottom: 3px;">
                      • ${media.originalname} (${(media.size / 1024 / 1024).toFixed(2)} MB)
                      ${media.credit ? ` - ${media.credit}` : ''}
                    </div>
                  `).join('')}
                </div>
                ` : ''}
              </div>
            `;
          }).join('')}
        </body>
        </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait for content to load, then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
        }, 250);
      };
    } catch (error) {
      console.error('Error exporting detailed PDF:', error);
      alert('Erreur lors de l\'export PDF détaillé');
    }
  };

  const sanitizeFileName = (fileName) => {
    return fileName.trim().replace(/[\#\%\&\{\}\<\>\*\?\/\$\!\'\"\ \:\@\+\\\|\=]/g, '_');
  };

  const handleDownloadPieceMedia = async (projet) => {
    const piece = projet.projet;
    if (!piece.uploadedMedia || piece.uploadedMedia.length === 0) {
      alert('Aucun média disponible pour cette pièce');
      return;
    }

    try {
      const zip = new JSZip();
      const mediaBaseUrl = process.env.REACT_APP_MEDIA_BASE_URL;
      
      // Show loading state
      const loadingMessage = document.createElement('div');
      loadingMessage.innerHTML = 'Téléchargement en cours... 0/' + piece.uploadedMedia.length;
      loadingMessage.style.cssText = 'position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);background:white;padding:20px;border-radius:8px;box-shadow:0 2px 10px rgba(0,0,0,0.2);z-index:9999';
      document.body.appendChild(loadingMessage);
      
      for (let i = 0; i < piece.uploadedMedia.length; i++) {
        const media = piece.uploadedMedia[i];
        loadingMessage.innerHTML = `Téléchargement en cours... ${i + 1}/${piece.uploadedMedia.length}`;
        
        try {
          const response = await fetch(`${mediaBaseUrl}/${media.path}`);
          if (!response.ok) {
            throw new Error(`Failed to fetch file: ${media.path}`);
          }
          const blob = await response.blob();
          
          // Create filename with piece name and media index
          const extension = media.originalname.split('.').pop();
          const zipFileName = sanitizeFileName(`${piece.name}_media_${i + 1}_${media.credit || ''}.${extension}`);
          zip.file(zipFileName, blob);
        } catch (err) {
          console.error(`Error fetching media ${media.path}:`, err);
        }
      }
      
      // Remove loading message
      document.body.removeChild(loadingMessage);
      
      // Generate and download zip
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = sanitizeFileName(`${piece.name}_medias.zip`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading piece media:', err);
      alert('Erreur lors du téléchargement des médias');
    }
  };

  const filteredProjects = useMemo(() => {
    return props.projets
      .sort(sortBy === "participationCreationDate" ? sortProjectsByParticipationCreationDate :
            sortBy === "companyName" ? sortProjectsByCompanyName : () => 0)
      .filter(projet => {
        if (!projet.projet.name || !projet.user.hsCompany?.properties?.name) return false;
        
        const status = getProjectStatus(projet.projet);
        if (statusFilter && status !== statusFilter) return false;
        if (typeFilter && projet.projet.type !== typeFilter) return false;
        if (categorieFilter && projet.projet.typologie !== categorieFilter) return false;
        if (verificationFilter && projet.projet.projet_verifie_fd100_2025 !== verificationFilter) return false;
        
        return true;
      });
  }, [props.projets, statusFilter, typeFilter, categorieFilter, verificationFilter, sortBy]);

  return (
    <ProjectsSection>
      <h3 style={{ marginBottom: '20px', color: '#333' }}>
        {props.event?.title_fr?.includes('Milan') ? 'Milan 2026 - Pièces soumises' : 'Sociétés et projets'}
      </h3>
      
      <FiltersContainer>
        <FilterGroup>
          <FilterLabel>Validation</FilterLabel>
          <FilterSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Tous</option>
            <option value="à valider">À valider</option>
            <option value="validé">Validé</option>
            <option value="refusé">Refusé</option>
            <option value="en attente">En attente</option>
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>Type</FilterLabel>
          <FilterSelect value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="">Tous</option>
            <option value="espace">Espace</option>
            <option value="objet">Objet</option>
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>Typologie</FilterLabel>
          <FilterSelect value={categorieFilter} onChange={(e) => setCategorieFilter(e.target.value)}>
            <option value="">Tous</option>
            {FDCategories.map((cat, i) => 
              <option value={cat.value} key={i}>{cat.label}</option>
            )}
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>Vérification</FilterLabel>
          <FilterSelect value={verificationFilter} onChange={(e) => setVerificationFilter(e.target.value)}>
            <option value="">Tous</option>
            <option value="oui">Vérifiés</option>
            <option value="non">Non vérifiés</option>
          </FilterSelect>
        </FilterGroup>
        
        <FilterGroup>
          <FilterLabel>Tri</FilterLabel>
          <FilterSelect value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="participationCreationDate">Date de création de la participation</option>
            <option value="companyName">Nom du studio</option>
          </FilterSelect>
        </FilterGroup>
      </FiltersContainer>

      <StatsContainer>
        <StatsText>{filteredProjects.length} projet(s) trouvé(s)</StatsText>
        <ExportContainer>
          <ExportButton className="excel" onClick={handleExportExcel}>
            📊 Export Excel
          </ExportButton>
          <ExportButton className="pdf" onClick={handleExportPDF}>
            📄 Export PDF
          </ExportButton>
          <ExportButton className="pdf" onClick={handleExportDetailedPDF}>
            📋 Export PDF détaillé
          </ExportButton>
        </ExportContainer>
      </StatsContainer>

      {filteredProjects.length === 0 ? (
        <NoProjectsMessage>
          Aucun projet trouvé avec les filtres sélectionnés.
        </NoProjectsMessage>
      ) : (
        <ProjectsTable>
          <TableHeader>
            <tr>
              <th>Entreprise</th>
              <th>{props.event?.title_fr?.includes('Milan') ? 'Pièce' : 'Projet'}</th>
              <th>Catégorie</th>
              <th>Aperçu</th>
              <th>Statut</th>
              <th>Actions</th>
            </tr>
          </TableHeader>
          <TableBody>
            {filteredProjects.map((projet) => {
              const status = getProjectStatus(projet.projet);
              const isVerified = projet?.projet?.projet_verifie_fd100_2025 === "oui";
              
              return (
                <ProjectRow
                  key={`participation-${projet.user.participation._id}-projet-${projet.projet._id}`}
                  verified={isVerified}
                >
                  {/* Company Column */}
                  <td>
                    <CompanyInfo>
                      <CompanyName>
                        <LinkButton
                          to={`/company?id=${projet.user.company?._id}`}
                          target='_blank'
                        >
                          {getEventDesignerDisplayName(projet.projet, projet.user)}
                        </LinkButton>
                      </CompanyName>

                      <CompanyLink
                        to={`/profile?id=${btoa(projet.user.user?.email || projet.user.userId?.email || '')}`}
                        target='_blank'
                      >
                        📋 Fiche candidat
                      </CompanyLink>

                      <ClickableButton
                        as="div"
                        onClick={() => handleCandidatureClick(projet.user.participation._id)}
                        style={{ cursor: 'pointer', fontSize: '12px', opacity: '0.8', marginTop: '4px' }}
                      >
                        📄 Candidature complète
                      </ClickableButton>

                      <CompanyTag>
                        {badCategoryVia(projet.user.hsCompany?.properties?.cat_gorie_via) && (
                          <CategoryWarning>
                            {badCategoryVia(projet.user.hsCompany?.properties?.cat_gorie_via)}
                          </CategoryWarning>
                        )}

                        {getDesignerTag(projet.user.hsCompany?.properties?.cat_gorie_via) && (
                          <DesignerTag>
                            {getDesignerTag(projet.user.hsCompany?.properties?.cat_gorie_via)}
                          </DesignerTag>
                        )}
                      </CompanyTag>
                    </CompanyInfo>
                  </td>

                  {/* Project/Piece Column */}
                  <td>
                    {projet.projet.name && (
                      <ProjectInfo>
                        <ProjectName>
                          {props.event?.title_fr?.includes('Milan') ? (
                            <ClickableButton 
                              onClick={() => handlePieceClick(projet.user.participation._id, projet.projet._id)}
                            >
                              {quentinCase(i18n.language !== 'fr' && projet.projet.name_en ? projet.projet.name_en : projet.projet.name)}
                            </ClickableButton>
                          ) : (
                            <LinkButton 
                              to={`/admin/events/fdficheprojet?email=${projet.user.user.email}&pid=${projet.user.participation._id}&projid=${projet.projet._id}&edit-mode=true`} 
                              target='_blank'
                            >
                              {quentinCase(i18n.language !== 'fr' && projet.projet.name_en ? projet.projet.name_en : projet.projet.name)}
                            </LinkButton>
                          )}
                          {projet.projet.fabrication_fr === "oui" && <FabFrIndicator />}
                        </ProjectName>
                        
                        {projet.projet.description && (
                          <ProjectDescription>
                            {projet.projet.description.substring(0, 120)}
                            {projet.projet.description.length > 120 && '...'}
                          </ProjectDescription>
                        )}
                        
                        {/* Show partner info for Milan 2026 pieces */}
                        {props.event?.title_fr?.includes('Milan') && (() => {
                          // Get partner information from the piece data
                          const designerName = projet.projet.designerCompanyName;
                          const fabricantName = projet.projet.fabricantCompanyName;
                          const submissionUserType = projet.projet.submissionUserType;
                          const mainCompanyName = getEventDesignerDisplayName(projet.projet, projet.user);
                          
                          // For designer submissions, show fabricant partner if different
                          if (submissionUserType === 'designer' && fabricantName && fabricantName !== mainCompanyName) {
                            return (
                              <PartnerInfo>
                                Fabricant - <strong>{fabricantName}</strong>
                              </PartnerInfo>
                            );
                          }
                          
                          // For fabricant submissions, show designer partner if different
                          if (submissionUserType === 'fabricant' && designerName && designerName !== mainCompanyName) {
                            return (
                              <PartnerInfo>
                                Designer - <strong>{designerName}</strong>
                              </PartnerInfo>
                            );
                          }
                          
                          return null;
                        })()}
                      </ProjectInfo>
                    )}
                  </td>

                  <td>
                    <CategorySelect 
                      value={projet.projet.typologie || ""} 
                      onChange={(e) => props.updateProjetCategorie(projet.user.participation._id, projet.projet._id, e.target.value)}
                    >
                      <option value="">Catégorie</option>
                      {FDCategories.map((cat, i) => 
                        <option value={cat.value} key={i}>{cat.label}</option>
                      )}
                    </CategorySelect>
                  </td>
                  
                  <td>
                    {(() => {
                      const mediaPreview = getMediaPreview(projet.projet);

                      if (mediaPreview) {
                        // Handle PDF with preview image
                        if (mediaPreview.isPdf) {
                          return (
                            <MediaPreview onClick={() => handlePieceClick(projet.user.participation._id, projet.projet._id)}>
                              <MediaImage
                                src={mediaPreview.url}
                                alt={projet.projet.name}
                                onError={(e) => {
                                  // If PDF preview image doesn't exist, show PDF icon
                                  e.target.style.display = 'none';
                                  e.target.parentNode.innerHTML = `
                                    <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; color: #adb5bd; font-size: 12px;">
                                      <div style="font-size: 32px; margin-bottom: 4px;">📄</div>
                                      <div>PDF</div>
                                    </div>
                                  `;
                                }}
                              />
                            </MediaPreview>
                          );
                        }
                        // Handle video with video element preview
                        else if (mediaPreview.isVideo) {
                          return (
                            <MediaPreview onClick={() => handlePieceClick(projet.user.participation._id, projet.projet._id)}>
                              <video
                                src={mediaPreview.url}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                muted
                                onError={(e) => {
                                  // If video can't load, show video icon
                                  e.target.style.display = 'none';
                                  e.target.parentNode.innerHTML = `
                                    <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; color: #adb5bd; font-size: 12px;">
                                      <div style="font-size: 32px; margin-bottom: 4px;">🎥</div>
                                      <div>Vidéo</div>
                                    </div>
                                  `;
                                }}
                              />
                            </MediaPreview>
                          );
                        }
                        // Handle other files with icon
                        else if (mediaPreview.isFile) {
                          const fileIcon = '📁';
                          return (
                            <MediaPreview onClick={() => handlePieceClick(projet.user.participation._id, projet.projet._id)}>
                              <MediaPlaceholder>
                                <MediaIcon style={{ fontSize: '32px' }}>{fileIcon}</MediaIcon>
                              </MediaPlaceholder>
                            </MediaPreview>
                          );
                        }
                        // Show image preview (default case)
                        else {
                          return (
                            <MediaPreview onClick={() => handlePieceClick(projet.user.participation._id, projet.projet._id)}>
                              <MediaImage
                                src={mediaPreview.url}
                                alt={projet.projet.name}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.parentNode.innerHTML = `
                                    <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; color: #adb5bd; font-size: 12px;">
                                      <div style="font-size: 24px; margin-bottom: 4px;">📁</div>
                                      <div>Média</div>
                                    </div>
                                  `;
                                }}
                              />
                            </MediaPreview>
                          );
                        }
                      }

                      return (
                        <MediaPreview>
                          <MediaPlaceholder>
                            <MediaIcon>🖼️</MediaIcon>
                            <div>Aucun média</div>
                          </MediaPlaceholder>
                        </MediaPreview>
                      );
                    })()}
                  </td>
                  
                  <td>
                    <StatusContainer className="status-container">
                      <StatusBadge
                        status={status}
                        onClick={() => setEditingStatus(editingStatus === `${projet.user.participation._id}-${projet.projet._id}` ? null : `${projet.user.participation._id}-${projet.projet._id}`)}
                      >
                        {status === 'à valider' ? 'À valider' :
                         status === 'en attente' ? 'En attente' :
                         status.charAt(0).toUpperCase() + status.slice(1)}
                      </StatusBadge>
                      {editingStatus === `${projet.user.participation._id}-${projet.projet._id}` && (
                        <StatusDropdown>
                          <StatusOption
                            className={status === 'à valider' ? 'selected' : ''}
                            onClick={() => handleStatusChange(projet.user.participation._id, projet.projet._id, 'à valider')}
                          >
                            À valider
                          </StatusOption>
                          <StatusOption
                            className={status === 'validé' ? 'selected' : ''}
                            onClick={() => handleStatusChange(projet.user.participation._id, projet.projet._id, 'validé')}
                          >
                            Validé
                          </StatusOption>
                          <StatusOption
                            className={status === 'refusé' ? 'selected' : ''}
                            onClick={() => handleStatusChange(projet.user.participation._id, projet.projet._id, 'refusé')}
                          >
                            Refusé
                          </StatusOption>
                          <StatusOption
                            className={status === 'en attente' ? 'selected' : ''}
                            onClick={() => handleStatusChange(projet.user.participation._id, projet.projet._id, 'en attente')}
                          >
                            En attente
                          </StatusOption>
                        </StatusDropdown>
                      )}
                    </StatusContainer>
                  </td>

                  <td>
                    {projet.projet.uploadedMedia && projet.projet.uploadedMedia.length > 0 && (
                      <ClickableButton
                        onClick={() => handleDownloadPieceMedia(projet)}
                        style={{
                          fontSize: '12px',
                          display: 'block',
                          textAlign: 'center',
                          padding: '4px 8px',
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                      >
                        💾 Médias ({projet.projet.uploadedMedia.length})
                      </ClickableButton>
                    )}
                  </td>
                </ProjectRow>
              );
            })}
          </TableBody>
        </ProjectsTable>
      )}

      {selectedPiece && (
        <PieceDetailsPopup 
          participationId={selectedPiece.participationId}
          pieceId={selectedPiece.pieceId}
          onClose={handleClosePiecePopup}
        />
      )}

      {selectedCandidature && (
        <CandidatureCompletePopup 
          participationId={selectedCandidature}
          onClose={handleCloseCandidaturePopup}
        />
      )}
    </ProjectsSection>
  );
};

export default ModernFDProjetsList;